import React from 'react'
import { Header } from '../components/Header'
import { Footer } from '../components/Footer'
import { SubServiceCard } from '../components/SubServiceCard'
import { CTASection } from '../components/CTASection'
import { trainingServices, trainingApproach } from '../data/mockData'
import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { ArrowLeftIcon, CheckCircle2Icon, GraduationCapIcon, UsersIcon, LightbulbIcon, ShieldCheckIcon, CpuIcon, BookOpenIcon, TargetIcon, LayersIcon, BoxIcon, LayoutIcon, MessageSquareIcon } from 'lucide-react'
import { SeoHelmet } from '../components/SeoHelmet'
import { ROUTE_META } from '../data/routeMeta'

export function Training() {
  return (
    <div className="min-h-screen w-full bg-white flex flex-col font-['Roboto']">
      <SeoHelmet {...ROUTE_META['/training']} url="/training" pageType="website" />
      <Header />

      <main className="flex-1">
        <section className="relative w-full bg-navy py-24 px-4 sm:px-6 lg:px-8 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://images.unsplash.com/photo-1524178232363-1fb2b075b655?ixlib=rb-4.0.3&auto=format&fit=crop&w=2000&q=80"
              alt="Team in a hands-on training session"
              className="w-full h-full object-cover opacity-20"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/90 to-transparent" />
          </div>

          <div className="max-w-5xl mx-auto relative z-10">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
            >
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 text-white font-['Poppins']">
                Training <span className="text-primary">with AI</span>
              </h1>
              <p className="text-xl sm:text-2xl text-gray-300 leading-relaxed max-w-3xl">
                We get your teams confident, safe, and genuinely productive with
                AI tools — from the fundamentals to embedding AI in everyday
                work.
              </p>
            </motion.div>
          </div>
        </section>

        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-12 text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 font-['Poppins']">
                How we can help
              </h2>
            </motion.div>

            <div className="space-y-8">
              {trainingServices.map((service, index) => (
                <SubServiceCard
                  key={index}
                  title={service.title}
                  description={service.description}
                  icon={service.icon}
                  delay={service.delay}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="w-full py-24 px-4 sm:px-6 lg:px-8 bg-white border-t border-gray-100">
          <div className="max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mb-16 text-center"
            >
              <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 font-['Poppins']">
                Our approach
              </h2>
              <p className="text-xl text-gray-600 max-w-2xl mx-auto font-['Roboto']">
                Training people remember and use — shaped around your tools,
                your teams, and the work they do every day.
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {trainingApproach.map((item, index) => (
                <motion.div
                  key={item.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.1 * (index + 1) }}
                  className="text-center"
                >
                  <div className="w-12 h-12 rounded-lg bg-light-background flex items-center justify-center text-primary mx-auto mb-5">
                    {item.icon}
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2 font-['Poppins']">
                    {item.title}
                  </h3>
                  <p className="text-gray-600 leading-relaxed font-['Roboto']">
                    {item.description}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="mt-16 rounded-xl bg-light-background border border-gray-200 px-6 py-8 text-center"
            >
              <p className="text-lg sm:text-xl font-medium text-gray-900 max-w-3xl mx-auto leading-relaxed font-['Poppins']">
                Backed by active-learning pedagogy, research shows AI-supported,
                active learning helps people learn more than twice as much in less time
                than traditional methods.
              </p>
            </motion.div>
          </div>
        </section>

        <CTASection />
      </main>

      <Footer />
    </div>
  )
}